"use client";

import { useEffect } from "react";

/**
 * Global error handler for JavaScript errors
 * Helps Googlebot identify rendering issues and prevents soft 404s
 * Based on Google's JavaScript SEO best practices:
 * https://developers.google.com/search/docs/crawling-indexing/javascript/fix-search-javascript
 */
export default function GlobalErrorHandler() {
  useEffect(() => {
    // Handle synchronous JavaScript errors
    const handleError = (event: ErrorEvent) => {
      // Log error details for debugging (only in development)
      if (process.env.NODE_ENV === "development") {
        console.error("JavaScript error:", {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
        });
      }

      // Prevent default error handling that might break rendering
      // Don't throw or re-throw errors that could affect Googlebot
      event.preventDefault();

      // In production, errors are silently handled to prevent rendering issues
      // This ensures Googlebot can still render the page even if non-critical JS fails
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Log rejection details for debugging (only in development)
      if (process.env.NODE_ENV === "development") {
        console.error("Unhandled promise rejection:", {
          reason: event.reason,
          promise: event.promise,
        });
      }

      // Prevent default handling
      event.preventDefault();
    };

    // Register error handlers
    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    // Cleanup
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return null;
}
